// Resources query layer. Pairs with `lib/views/resources.ts` (server fetchers
// used during SSR prefetch) and `lib/mutations/resources.ts`.
// No 'use client' directive: hooks are React hooks called from client
// components; server pages import the query keys to prefetch.
import {
  parseReactionCounts,
  parseReactionState,
  type ReactionCounts,
  type ReactionKind,
} from '@leanin/shared/reactions';

import { useEffect, useMemo } from 'react';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { ResourceDesignedFor } from '@leanin/shared/resource-designed-for';
import type { PageMetaLoaderData } from '../analytics/page-meta';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';
import { shouldRetry } from '../query-retry';
import { editorialTitleSubject } from '../seo-text';
import { loadCurrentForRoute } from './load-for-route';
import { isSearchModeChangedError } from './search';
import { QUERY_KEY_REGISTRY, sortedStrings, trimLower } from './normalize';

// ---- Public types ----

export type ResourceKind =
  | 'article'
  | 'video'
  | 'toolkit'
  | 'workshop'
  | 'essay'
  | 'discussion_guide'
  | 'research'
  | 'podcast'
  | 'activity'
  | 'playlist';

// Real server-side content scopes. The Resources tabs ALSO expose UI-only
// tab ids (`home`, `saved` — see `RESOURCE_TAB_SCOPES`)
// handled in the body via content swaps, never passed through to the
// /resources endpoint. The server's `resources_scope_check` rejects them;
// `resourceListFiltersFromSearch` maps them to no `scope` param.
export type ResourceScope =
  | 'leadership_library'
  | 'circle_leader_tips'
  | 'network_leader_resources';

// The Resources landing tabs — single source of truth shared by the URL
// contract (`components/resources/resource-filters.ts`) and the query
// mapping below. A past split kept two copies of this list that drifted;
// keep it defined exactly once.
export const RESOURCE_TAB_SCOPES = [
  'home',
  'leadership_library',
  'circle_leader_tips',
  'network_leader_resources',
  'saved',
] as const;
export type ResourceTabScope = (typeof RESOURCE_TAB_SCOPES)[number];

// URL aliases → canonical tab. Hyphenated forms cover shared/bookmarked
// links; `all` and the learning-tracks forms are retired tabs that degrade
// to the Home landing.
export const RESOURCE_TAB_SCOPE_ALIASES: Record<string, ResourceTabScope> = {
  'leadership-library': 'leadership_library',
  'circle-leader-tips': 'circle_leader_tips',
  'network-leader-resources': 'network_leader_resources',
  all: 'home',
  'learning-tracks': 'home',
  learning_tracks: 'home',
};

export function normalizeResourceTabScope(
  raw: string | undefined,
): ResourceTabScope {
  const value = raw ?? 'home';
  if ((RESOURCE_TAB_SCOPES as ReadonlyArray<string>).includes(value)) {
    return value as ResourceTabScope;
  }
  return RESOURCE_TAB_SCOPE_ALIASES[value] ?? 'home';
}

// Which audience the content was made for. `null` on a summary means not yet
// reviewed: no label, and never treated as for everyone (`all_users`).
export type { ResourceDesignedFor };

export type ResourceContentLayer =
  | 'community'
  | 'network'
  | 'public';

export type ResourceSort = 'featured' | 'newest';

// The wire-shape of a list-page item, lifted out of the generated openapi
// types. We keep this as a structural type so the same shape covers both the
// list and search-hit views (search wraps it under `.resource`).
export type ResourceSummaryView = {
  id: string;
  title: string;
  kind: ResourceKind;
  authorUserId: string;
  author: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl: string | null;
    jobTitle: string | null;
    company: string | null;
  } | null;
  lede: string | null;
  coverImageUrl: string | null;
  publishedAt: string | null;
  featuredAt: string | null;
  readingTimeMinutes: number | null;
  tags: ReadonlyArray<{ slug: string; label: string }>;
  reactionCounts: ReactionCounts;
  hidden: boolean;
  scope: ResourceScope;
  designedFor: ResourceDesignedFor | null;
  // Absolute leanin.org URL of the original article when this Connect
  // resource is a republication of a leanin.org page. Null otherwise.
  // Admin-authored; drives the rel=canonical tag and the on-page
  // "originally published on …" attribution line.
  canonicalUrl: string | null;
};

export type ResourceListPage = {
  items: ResourceSummaryView[];
  nextCursor: string | null;
  pageSize: number;
};

export type ResourceSearchPage = components['schemas']['ResourceSearchPage'];

type ResourceSummaryWire = components['schemas']['ResourceSummary'];

// Every resource the API sends goes through this (or, for a detail, which
// also carries the viewer's reaction, `parseReactionState`) on arrival, so the
// cache and every render site hold only the reaction kinds this bundle knows.
// Generic so a summary nested in a larger shape keeps that shape's type.
export function parseResourceSummary<R extends ResourceSummaryWire>(raw: R): R {
  return { ...raw, reactionCounts: parseReactionCounts(raw.reactionCounts) };
}

export type ResourceTagView = {
  slug: string;
  label: string;
  description: string | null;
  resourceCount: number;
};

export type TopicDomain = 'content' | 'leader_stage' | 'shelf';

// Attachments are a discriminated union; uploaded media (image/file) carries
// a mimeType, embedded video carries a provider. Mirrors `ResourceAttachment`
// in the server.
export type ResourceAttachmentView =
  | { kind: 'image'; url: string; mimeType: string; name?: string | null }
  | { kind: 'video'; provider: 'youtube' | 'vimeo'; url: string }
  | { kind: 'video'; provider: 'upload'; url: string; mimeType: string; name: string | null }
  | { kind: 'file'; url: string; mimeType: string; name?: string | null };

// Detail shape — mirrors `operations['getResource']['responses'][200]…`.
export type ResourceDetailView = {
  id: string;
  title: string;
  kind: ResourceKind;
  authorUserId: string;
  author: ResourceSummaryView['author'];
  lede: string | null;
  coverImageUrl: string | null;
  // Retained pre-crop original + the crop rect (source pixels of the
  // original) that produced the render. Null on legacy rows.
  coverImageOriginalUrl: string | null;
  coverImageCrop: { x: number; y: number; width: number; height: number } | null;
  publishedAt: string | null;
  featuredAt: string | null;
  tags: ReadonlyArray<{ slug: string; label: string }>;
  reactionCounts: ResourceSummaryView['reactionCounts'];
  hidden: boolean;
  // Source markdown. The article view renders it through `ArticleBody`, as
  // does the server-composed body for anonymous visitors, and the editor
  // edits it.
  bodyMarkdown: string;
  // The API's own sanitized HTML rendering of `bodyMarkdown`. Nothing in this
  // app renders it: the article view renders the markdown, and the PDF
  // download is built from the markdown's syntax tree on the server.
  bodyHtml: string;
  // Headings extracted from the rendered body, in document order, for the
  // article-view "On this page" sidebar.
  sections: ReadonlyArray<{ id: string; text: string; level: number }>;
  attachments: ReadonlyArray<ResourceAttachmentView>;
  contentLayer: ResourceContentLayer;
  scope: ResourceScope;
  viewerSaved: boolean;
  viewerReaction: ReactionKind | null;
  showAuthor: boolean;
  designedFor: ResourceSummaryView['designedFor'];
  canonicalUrl: ResourceSummaryView['canonicalUrl'];
  commentCount: number;
  attachmentsVersion: string;
};

export type ResourceListFilters = {
  scope?: ResourceScope;
  // One or more resource kinds, sent to the server as a comma-separated
  // `contentType`. The kind dropdown sets a single value; the "Self serve"
  // tab sets several (video + article).
  kinds?: ReadonlyArray<ResourceKind>;
  tags?: ReadonlyArray<string>;
  designedFor?: ResourceDesignedFor;
  saved?: boolean;
  featured?: boolean;
  // Pure recency ordering — used by the Home "New releases" rail. The grid
  // keeps the library's default featured-first order.
  sort?: 'newest';
  // Only resources published within the last N days (server clock).
  publishedWithinDays?: number;
  q?: string;
  pageSize?: number;
};

// The /resources/search inputs. Search carries the SAME tag/scope/saved
// filters as the browse list (#435/#436): the endpoint composes them into the
// retrieval core, so a filter narrows results whether or not a query is typed.
export type ResourceSearchFilters = {
  q: string;
  pageSize?: number;
  scope?: ResourceScope;
  tags?: ReadonlyArray<string>;
  designedFor?: ResourceDesignedFor;
  saved?: boolean;
};

// One shelf of the Guides / Tips landing — a topic (or tips stage) with its
// first page of resources and the scope-wide total for "See all (N)".
export type ResourceShelfView = {
  topic: { slug: string; label: string; description: string | null };
  totalCount: number;
  items: ResourceSummaryView[];
};

export type ResourceShelvesScope =
  | 'leadership_library'
  | 'circle_leader_tips'
  | 'collections';

// Picker source: a small, lightly-shaped list for "attach a resource" typeahead
// (and the related-resources aside). Same fields the Circle resource-picker
// needs (id, title, kind, publishedAt).
export type ResourcePickerOption = {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly publishedAt: string | null;
  readonly designedFor: ResourceDesignedFor | null;
};

// Related-resources aside item — the resource detail page's "you might also
// like" list. Structurally `{ id, title }`.
export type RelatedResourceView = {
  readonly id: string;
  readonly title: string;
};

// One resource comment — flat (no threading), author preview joined
// server-side. `authorName` is null when the author row is erased.
export type ResourceCommentView = {
  readonly id: string;
  readonly resourceId: string;
  readonly authorUserId: string;
  readonly authorName: string | null;
  readonly authorPhotoUrl: string | null;
  readonly body: string;
  readonly bodyFormat: 'v1' | 'v2';
  readonly editedAt: string | null;
  readonly createdAt: string;
};

export type ResourceCommentsPage = {
  readonly items: ReadonlyArray<ResourceCommentView>;
  readonly nextCursor: string | null;
};

export type CircleResourceAttachment =
  components['schemas']['CircleResourceAttachment'];

export type CircleResourceAttachmentsPage = {
  items: ReadonlyArray<CircleResourceAttachment>;
  nextCursor: string | null;
  pageSize: number;
};

export type TopicView = components['schemas']['TopicView'];

// ---- Query keys + leaf factories ----
//
// Inner nodes (`all`, `lists`, `searches`) stay bare `as const` arrays — they
// are invalidation prefixes. Leaf entries return `queryOptions` /
// `infiniteQueryOptions` whose key is built adjacent to the `queryFn` from one
// normalized `args` object (design decision 3). The fetcher trusts the
// normalized input and re-normalizes nothing.

export const resourcesKeys = {
  all: ['resources'] as const,
  lists: () => [...resourcesKeys.all, 'list'] as const,
  searches: () => [...resourcesKeys.all, 'search'] as const,

  // Infinite list leaf. `filters` is the terminal key element; the cursor is
  // injected via `pageParam` and never enters the key.
  list: (filters: ResourceListFilters) =>
    infiniteQueryOptions({
      queryKey: [...resourcesKeys.lists(), filters] as const,
      queryFn: ({ pageParam }) =>
        fetchResources({ ...filters, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    }),

  // Infinite search leaf. `params` (terminal) is the sole fetch input besides
  // the cursor. `enabled` depends on call-site state, so it is spread at the
  // hook, not baked into the leaf (decision 7).
  search: (params: ResourceSearchFilters) =>
    infiniteQueryOptions({
      queryKey: [...resourcesKeys.searches(), params] as const,
      queryFn: ({ pageParam }) =>
        fetchResourceSearch({
          ...params,
          ...(pageParam !== undefined ? { cursor: pageParam } : {}),
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
      retry: (failureCount, error) =>
        !isSearchModeChangedError(error) && shouldRetry(failureCount, error),
    }),

  // Picker typeahead leaf — trim+lowercase ONCE here; `fetchResourcesForPicker`
  // receives the normalized query and does not re-normalize it.
  picker: (rawQuery: string) => {
    const q = trimLower(rawQuery);
    return queryOptions({
      queryKey: [...resourcesKeys.all, 'picker', q] as const,
      queryFn: () => fetchResourcesForPicker(q),
      staleTime: 30 * 1000,
    });
  },

  detail: (id: string) =>
    queryOptions({
      queryKey: [...resourcesKeys.all, 'detail', id] as const,
      queryFn: () => fetchResourceDetail(id),
    }),

  // Infinite comments leaf for a resource detail page — newest-first pages,
  // cursor rides `pageParam` and never enters the key.
  comments: (resourceId: string) =>
    infiniteQueryOptions({
      queryKey: [...resourcesKeys.all, 'comments', resourceId] as const,
      queryFn: ({ pageParam }) =>
        fetchResourceComments(resourceId, pageParam ?? undefined),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  // Related-resources aside leaf for a resource detail page. `excludeId` is an
  // id segment; `tagSlug` (normalized to the key) drives the fetch.
  related: (excludeId: string, raw?: { tagSlug?: string }) => {
    const args = { tagSlug: raw?.tagSlug ?? null };
    return queryOptions({
      queryKey: [...resourcesKeys.all, 'related', excludeId, args] as const,
      queryFn: () => fetchRelatedResources(excludeId, args),
    });
  },

  tags: (domains?: ReadonlyArray<TopicDomain>, scope?: ResourceScope) =>
    queryOptions({
      queryKey: [
        ...resourcesKeys.all,
        'tags',
        domains ?? ['content'],
        scope ?? 'all',
      ] as const,
      queryFn: () => fetchResourceTags(domains, scope),
      staleTime: 5 * 60 * 1000,
    }),

  shelves: (scope: ResourceShelvesScope) =>
    queryOptions({
      queryKey: [...resourcesKeys.all, 'shelves', scope] as const,
      queryFn: () => fetchResourceShelves(scope),
      staleTime: 60 * 1000,
    }),

  topics: (domains?: ReadonlyArray<TopicDomain>) =>
    queryOptions({
      queryKey: [...resourcesKeys.all, 'topics', domains ?? ['all']] as const,
      queryFn: () => fetchTopics(domains),
      staleTime: 5 * 60 * 1000,
    }),

  savedIds: () =>
    queryOptions({
      queryKey: [...resourcesKeys.all, 'me', 'saved-ids'] as const,
      queryFn: () => fetchMySavedResourceIds(),
    }),

  // Invalidation prefix for the recommended rail — the leaf composes this
  // exact node so prefix invalidation cannot silently miss (the
  // network-circles lesson).
  recommendedAll: () => [...resourcesKeys.all, 'me', 'recommended'] as const,
  recommended: (limit: number) =>
    queryOptions({
      queryKey: [...resourcesKeys.recommendedAll(), limit] as const,
      queryFn: () => fetchRecommendedResources(limit),
      staleTime: 5 * 60 * 1000,
    }),

  circleAttachments: (circleId: string) =>
    queryOptions({
      queryKey: [
        ...resourcesKeys.all,
        'circle',
        circleId,
        'attachments',
      ] as const,
      queryFn: () => fetchCircleResources({ circleId }),
    }),
};

// Predicate for save/unsave cache invalidation. Co-located with the filter
// shape it inspects (see `resourceScopeTagsSavedFromSearch` below, composed
// into `resourceListFiltersFromSearch`) so the two can't drift — the
// predicate's coupling to `filters.saved === true` is the inverse of that
// builder's `apiSaved ? { saved: true } : {}` line. Drift here silently broke
// the saved-tab unsave flow once already.
export function isSavedScopeListKey(key: readonly unknown[]): boolean {
  if (key[0] !== 'resources' || key[1] !== 'list') return false;
  const filters = key[2] as ResourceListFilters | undefined;
  return filters?.saved === true;
}

// ---- Client fetchers ----

export async function fetchResources(
  params: ResourceListFilters & { cursor?: string },
): Promise<ResourceListPage> {
  const client = getApiClient();
  const tagsParam =
    params.tags && params.tags.length > 0 ? params.tags.join(',') : undefined;
  const contentTypeParam =
    params.kinds && params.kinds.length > 0 ? params.kinds.join(',') : undefined;
  const data = unwrap(await client.GET('/api/v1/resources', {
    params: {
      query: {
        ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        ...(params.scope !== undefined ? { scope: params.scope } : {}),
        ...(contentTypeParam !== undefined ? { contentType: contentTypeParam } : {}),
        ...(tagsParam !== undefined ? { tags: tagsParam } : {}),
        ...(params.designedFor !== undefined ? { designedFor: params.designedFor } : {}),
        ...(params.saved === true ? { saved: 'true' as const } : {}),
        ...(params.featured === true ? { featured: 'true' as const } : {}),
        ...(params.sort !== undefined ? { sort: params.sort } : {}),
        ...(params.publishedWithinDays !== undefined
          ? { publishedWithinDays: params.publishedWithinDays }
          : {}),
        ...(params.q !== undefined && params.q.length > 0 ? { q: params.q } : {}),
      },
    },
  }));
  return { ...data, items: data.items.map(parseResourceSummary) } as ResourceListPage;
}

export async function fetchResourceShelves(
  scope: ResourceShelvesScope,
): Promise<readonly ResourceShelfView[]> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/resources/shelves', {
    params: { query: { scope } },
  }));
  return data.shelves.map((shelf) => ({
    ...shelf,
    items: shelf.items.map(parseResourceSummary),
  })) as ResourceShelfView[];
}

export async function fetchResourceSearch(
  params: ResourceSearchFilters & { cursor?: string },
): Promise<ResourceSearchPage> {
  const client = getApiClient();
  const tagsParam =
    params.tags && params.tags.length > 0 ? params.tags.join(',') : undefined;
  const data = unwrap(await client.GET('/api/v1/resources/search', {
    params: {
      query: {
        q: params.q,
        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
        ...(params.scope !== undefined ? { scope: params.scope } : {}),
        ...(tagsParam !== undefined ? { tags: tagsParam } : {}),
        ...(params.designedFor !== undefined ? { designedFor: params.designedFor } : {}),
        ...(params.saved === true ? { saved: 'true' as const } : {}),
      },
    },
  }));
  return {
    ...data,
    items: data.items.map((hit) => ({
      ...hit,
      resource: parseResourceSummary(hit.resource),
    })),
  };
}

export async function fetchResourceDetail(
  id: string,
): Promise<ResourceDetailView> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/resources/{id}', {
    params: { path: { id } },
  }));
  return parseReactionState(data.data) as ResourceDetailView;
}

export async function fetchResourceComments(
  resourceId: string,
  cursor?: string,
): Promise<ResourceCommentsPage> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/resources/{id}/comments', {
    params: {
      path: { id: resourceId },
      query: { pageSize: 20, ...(cursor !== undefined ? { cursor } : {}) },
    },
  }));
  return {
    items: data.items.map((c) => ({
      id: c.id,
      resourceId: c.resourceId,
      authorUserId: c.authorUserId,
      authorName: c.authorName,
      authorPhotoUrl: c.authorPhotoUrl,
      body: c.body,
      bodyFormat: c.bodyFormat,
      editedAt: c.editedAt,
      createdAt: c.createdAt,
    })),
    nextCursor: data.nextCursor,
  };
}

export async function fetchResourceTags(
  domains?: ReadonlyArray<TopicDomain>,
  scope?: ResourceScope,
): Promise<readonly ResourceTagView[]> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/resource-tags', {
    params: {
      query: {
        ...(domains !== undefined && domains.length > 0
          ? { domain: domains.join(',') }
          : {}),
        ...(scope !== undefined ? { scope } : {}),
      },
    },
  }));
  return data;
}

export async function fetchTopics(
  domains?: ReadonlyArray<TopicDomain>,
): Promise<readonly TopicView[]> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/topics', {
    params: {
      query: {
        ...(domains !== undefined && domains.length > 0
          ? { domain: domains.join(',') }
          : {}),
      },
    },
  }));
  return data;
}

// Related resources for a resource's "you might also like" aside: up to 5
// others sharing the resource's first topic, any kind. Receives the
// already-normalized `tagSlug` from the `related` leaf (no re-derivation
// here).
export async function fetchRelatedResources(
  excludeId: string,
  args: { tagSlug: string | null },
): Promise<readonly RelatedResourceView[]> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/resources', {
    params: {
      query: {
        pageSize: 6,
        ...(args.tagSlug !== null ? { tags: args.tagSlug } : {}),
      },
    },
  }));
  return data.items
    .filter((r) => r.id !== excludeId)
    .slice(0, 5)
    .map((r) => ({ id: r.id, title: r.title }));
}

export async function fetchMySavedResourceIds(
  resourceIds?: ReadonlyArray<string>,
): Promise<readonly string[]> {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/me/resources/saved-ids',
    {
      params: {
        query:
          resourceIds !== undefined && resourceIds.length > 0
            ? { resourceIds: resourceIds.join(',') }
            : {},
      },
    },
  ));
  return data.ids;
}

export async function fetchRecommendedResources(
  limit: number,
): Promise<readonly ResourceSummaryView[]> {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/me/resources/recommended',
    { params: { query: { limit } } },
  ));
  return data.items.map(parseResourceSummary) as readonly ResourceSummaryView[];
}

// ---- Hooks ----

export function useInfiniteResources(filters: ResourceListFilters) {
  return useInfiniteQuery(resourcesKeys.list(filters));
}

// The search endpoint rejects queries under 2 chars (422); below that the
// resources body stays on the plain list query.
export const MIN_RESOURCE_SEARCH_LENGTH = 2;

export function useInfiniteResourceSearch(params: ResourceSearchFilters) {
  const queryClient = useQueryClient();
  // Capture-once (decision 9): bind the options object once, consume it, and
  // reset against `opts.queryKey` — never re-invoke the factory at the reset
  // site (that would risk a key built from divergent inputs).
  const opts = resourcesKeys.search(params);
  const query = useInfiniteQuery({
    ...opts,
    enabled: params.q.length >= MIN_RESOURCE_SEARCH_LENGTH,
  });

  // 409 SEARCH_MODE_CHANGED: the cursor was minted under a different
  // retrieval mode (embedder degraded/recovered between pages). Reset
  // pagination silently — resetQueries drops every loaded page and
  // refetches page 1 of the active query (search-platform D9).
  const modeChanged = isSearchModeChangedError(query.error);
  useEffect(() => {
    if (!modeChanged) return;
    void queryClient.resetQueries({ queryKey: opts.queryKey, exact: true });
  }, [modeChanged, queryClient, opts.queryKey]);

  return query;
}

// ---- URL search → query inputs (single source of truth) ----
//
// Open input shape used by both the route's validateSearch output and the
// body's useFilterParams `values`. `ReadonlyArray<string>` is assignable
// to the broader `tags` array shape. Topic is the landing page's only
// facet — the server's kind/sort params remain available to other callers
// via `ResourceListFilters` directly.

export type ResourcesSearchInput = {
  q?: string;
  scope?: string;
  tags?: ReadonlyArray<string>;
  /** Demographic filter: the audience a resource was designed for. */
  designedFor?: ResourceDesignedFor | '';
  /** "See all" shelf page — one topic slug rendered as a titled grid. */
  shelf?: string;
};

const RESOURCES_DEFAULT_PAGE_SIZE = 24;

// Virtual shelf id — "the whole library, newest first". Not a topic slug;
// reserved so a real topic can't shadow it.
export const NEW_RELEASES_SHELF_SLUG = 'new-releases';

// The one shelf topic that is a landing-page rail rather than a collection
// folder — Home renders it as its own row and the Collections rail excludes
// it. Forms use the same split for "Library rows" vs "Collections".
export const GETTING_STARTED_SHELF_SLUG = 'getting-started';

export function resourceListFiltersFromSearch(
  search: ResourcesSearchInput,
): ResourceListFilters {
  const scope = normalizeResourceTabScope(search.scope);
  // A shelf page fetches the shelf topic's resources across the whole
  // library — the shelf slug wins over any lingering tag filter, and no
  // scope narrows it. Typing a query leaves the shelf (handled in the
  // body), so search behavior is unchanged. `new-releases` is a virtual
  // shelf: the library shelf by recency, no topic behind it (tips and
  // network-leader resources live on their own surfaces — mirrors the
  // Home rail's query).
  const shelf = search.shelf?.trim() ?? '';
  const designedFor = designedForFromSearch(search);
  if (shelf === NEW_RELEASES_SHELF_SLUG) {
    return {
      pageSize: RESOURCES_DEFAULT_PAGE_SIZE,
      scope: 'leadership_library',
      sort: 'newest',
      ...designedFor,
    };
  }
  if (shelf.length > 0) {
    return { pageSize: RESOURCES_DEFAULT_PAGE_SIZE, tags: [shelf], ...designedFor };
  }
  return {
    pageSize: RESOURCES_DEFAULT_PAGE_SIZE,
    ...resourceScopeTagsSavedFromSearch(search),
  };
}

// Scope / tags / saved derivation shared by BOTH the browse-list mapping and
// the search mapping — the ONE place the URL tab + topic filters translate to
// server filters, so browse and search narrow identically (#435/#436). Shelf
// handling stays in `resourceListFiltersFromSearch` (shelf mode is browse-only;
// typing a query leaves it, so search never derives a shelf).
//
// `home` and `saved` are UI-only tab ids — `home` searches the whole library
// (no scope), `saved` maps to the `saved` flag. Any tab that isn't a real
// server scope maps to no `scope` param.
function resourceScopeTagsSavedFromSearch(search: ResourcesSearchInput): {
  scope?: ResourceScope;
  tags?: ReadonlyArray<string>;
  designedFor?: ResourceDesignedFor;
  saved?: boolean;
} {
  const scope = normalizeResourceTabScope(search.scope);
  const tags = sortedStrings(
    (search.tags ?? []).map((s) => s.trim()).filter((s) => s.length > 0),
  );
  const apiSaved = scope === 'saved';
  const apiContentScope: ResourceScope | undefined =
    scope === 'leadership_library' ||
    scope === 'circle_leader_tips' ||
    scope === 'network_leader_resources'
      ? scope
      : undefined;
  return {
    ...(apiContentScope !== undefined ? { scope: apiContentScope } : {}),
    ...(tags.length > 0 ? { tags } : {}),
    ...designedForFromSearch(search),
    ...(apiSaved ? { saved: true } : {}),
  };
}

// The designed-for narrowing applies on every browse surface, the shelf page
// included — unlike tags, which a shelf slug overrides.
function designedForFromSearch(search: ResourcesSearchInput): {
  designedFor?: ResourceDesignedFor;
} {
  return search.designedFor !== undefined && search.designedFor !== ''
    ? { designedFor: search.designedFor }
    : {};
}

export function resourceSearchParamsFromSearch(
  search: ResourcesSearchInput,
): ResourceSearchFilters {
  return {
    q: (search.q ?? '').trim(),
    pageSize: RESOURCES_DEFAULT_PAGE_SIZE,
    ...resourceScopeTagsSavedFromSearch(search),
  };
}

export function useInfiniteResourcesFromSearch(search: ResourcesSearchInput) {
  const listFilters = useMemo(
    () => resourceListFiltersFromSearch(search),
    [search],
  );
  const searchParams = useMemo(
    () => resourceSearchParamsFromSearch(search),
    [search],
  );
  const listQuery = useInfiniteResources(listFilters);
  const searchQuery = useInfiniteResourceSearch(searchParams);
  // Below the search minimum the body keeps rendering the plain list — a
  // 1-char query is a transient typing state, not a search.
  const isSearching = searchParams.q.length >= MIN_RESOURCE_SEARCH_LENGTH;
  // `activeQuery` is the in-flight one for the current URL — used by the route
  // to gate the first-paint skeleton. Both queries share the .isPending / .isFetching
  // surface that the route reads; the body uses listQuery/searchQuery directly
  // because their data shapes differ.
  const activeQuery = isSearching ? searchQuery : listQuery;
  return { listQuery, searchQuery, isSearching, activeQuery };
}

export function useResourceDetail(
  id: string,
  options?: { retryOnMount?: boolean; retry?: false },
) {
  return useQuery({ ...resourcesKeys.detail(id), ...options });
}

// The resource page's loader data. Nothing when the read failed: the page
// renders that failure from the cache entry the read left behind.
export async function loadResourceMeta(
  queryClient: QueryClient,
  id: string,
): Promise<PageMetaLoaderData> {
  const resource = await loadCurrentForRoute(queryClient, resourcesKeys.detail(id));
  return resource === undefined ? {} : { pageTitle: editorialTitleSubject(resource) };
}

export function useResourceComments(
  resourceId: string,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({ ...resourcesKeys.comments(resourceId), ...options });
}

export function useResourceTags(
  domains?: ReadonlyArray<TopicDomain>,
  scope?: ResourceScope,
) {
  return useQuery(resourcesKeys.tags(domains, scope));
}

export function useResourceShelves(scope: ResourceShelvesScope) {
  return useQuery(resourcesKeys.shelves(scope));
}

export function useTopics(domains?: ReadonlyArray<TopicDomain>, options?: { readonly enabled?: boolean }) {
  return useQuery({ ...resourcesKeys.topics(domains), ...options });
}

export function useMySavedResourceIds(options?: { enabled?: boolean }) {
  return useQuery({ ...resourcesKeys.savedIds(), ...options });
}

export function useRecommendedResources(
  limit: number,
  options?: { enabled?: boolean },
) {
  return useQuery({ ...resourcesKeys.recommended(limit), ...options });
}

// Picker source: a small, lightly-shaped list for "attach a resource"
// typeahead. Fetches a small page server-side, filters by title/lede substring
// client-side — keeps the surface tiny without a dedicated endpoint. Receives
// the already-normalized (trimmed + lowercased) query from the `picker` leaf;
// does NOT re-normalize.
async function fetchResourcesForPicker(
  query: string,
): Promise<ReadonlyArray<ResourcePickerOption>> {
  const client = getApiClient();
  const data = unwrap(
    await client.GET('/api/v1/resources', {
      params: { query: { pageSize: 50 } },
    }),
  );
  const items =
    query.length === 0
      ? data.items.slice(0, 8)
      : data.items
          .filter((r) => {
            if (r.title.toLowerCase().includes(query)) return true;
            const lede = r.lede ?? '';
            return lede.toLowerCase().includes(query);
          })
          .slice(0, 8);
  return items.map((r) => ({
    id: r.id,
    title: r.title,
    kind: r.kind,
    publishedAt: r.publishedAt,
    designedFor: r.designedFor,
  }));
}

export function useResourcesForPicker(query: string, enabled = true) {
  return useQuery({ ...resourcesKeys.picker(query), enabled });
}

// Resources attached to a specific Circle. Goes through the typed API client
// so the dev-bypass `X-Dev-User-Id` header is injected by `devBypassFetch`;
// raw `fetch()` here would skip that wrapper and 401 in local dev.
export async function fetchCircleResources(params: {
  readonly circleId: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}): Promise<CircleResourceAttachmentsPage> {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/circles/{circleId}/resources',
    {
      params: {
        path: { circleId: params.circleId },
        query: {
          ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
          ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
        },
      },
    },
  ));
  return data;
}

export function useCircleResources(circleId: string) {
  return useQuery(resourcesKeys.circleAttachments(circleId));
}

// ---- Query-key registry (decision 6 invariant test) ----

QUERY_KEY_REGISTRY.push(
  {
    name: 'resourcesKeys.list',
    sampleKey: resourcesKeys.list({
      scope: 'leadership_library',
      kinds: ['article'],
      tags: ['leadership'],
      designedFor: 'all_users',
      saved: true,
      featured: true,
      q: 'mentorship',
      pageSize: 24,
    }).queryKey,
  },
  {
    name: 'resourcesKeys.search',
    sampleKey: resourcesKeys.search({
      q: 'mentorship',
      pageSize: 24,
      scope: 'leadership_library',
      tags: ['leadership'],
      designedFor: 'all_users',
      saved: true,
    }).queryKey,
  },
  {
    name: 'resourcesKeys.picker',
    sampleKey: resourcesKeys.picker('  Leadership  ').queryKey,
  },
  {
    name: 'resourcesKeys.detail',
    sampleKey: resourcesKeys.detail('resource-1').queryKey,
  },
  {
    name: 'resourcesKeys.comments',
    sampleKey: resourcesKeys.comments('resource-1').queryKey,
  },
  {
    name: 'resourcesKeys.related',
    sampleKey: resourcesKeys.related('resource-1', {
      tagSlug: 'leadership',
    }).queryKey,
  },
  { name: 'resourcesKeys.tags', sampleKey: resourcesKeys.tags().queryKey },
  {
    name: 'resourcesKeys.shelves',
    sampleKey: resourcesKeys.shelves('leadership_library').queryKey,
  },
  { name: 'resourcesKeys.topics', sampleKey: resourcesKeys.topics().queryKey },
  {
    name: 'resourcesKeys.savedIds',
    sampleKey: resourcesKeys.savedIds().queryKey,
  },
  {
    name: 'resourcesKeys.circleAttachments',
    sampleKey: resourcesKeys.circleAttachments('circle-1').queryKey,
  },
);

